import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './App.css';

gsap.registerPlugin(ScrollTrigger);

function App() {
  const [isDark, setIsDark] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const heroRef = useRef(null);
  const sectionsRef = useRef([]);
  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);

  useEffect(() => {
    // Custom cursor movement
    const handleMouseMove = (e) => {
      
      // Animate cursor with GSAP for smooth trailing
      if (cursorRef.current) {
        gsap.to(cursorRef.current, {
          x: e.clientX - 20,
          y: e.clientY - 20,
          duration: 0.5,
          ease: "power2.out"
        });
      }
      if (cursorDotRef.current) {
        gsap.to(cursorDotRef.current, {
          x: e.clientX - 4,
          y: e.clientY - 4,
          duration: 0.1
        });
      }
    };

    // Scroll progress
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    // Magnetic button effect
    const magneticBtns = document.querySelectorAll('.magnetic-btn');
    magneticBtns.forEach(btn => {
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        gsap.to(btn, {
          x: x * 0.3,
          y: y * 0.3,
          duration: 0.3,
          ease: "power2.out"
        });
      });
      btn.addEventListener('mouseleave', () => {
        gsap.to(btn, {
          x: 0,
          y: 0,
          duration: 0.5,
          ease: "elastic.out(1, 0.3)"
        });
      });
    });

    // Animated counters
    const animateCounters = () => {
      document.querySelectorAll('.stat-number').forEach(counter => {
        const target = counter.getAttribute('data-count');
        if (!target) return;
        
        gsap.fromTo(counter, 
          { innerText: 0 },
          {
            innerText: target,
            duration: 2,
            ease: "power2.out",
            snap: { innerText: 1 },
            scrollTrigger: {
              trigger: counter,
              start: "top 80%",
              toggleActions: "play none none none"
            },
            onUpdate: function() {
              counter.innerText = Math.ceil(counter.innerText) + (target.includes('+') ? '+' : '');
            }
          }
        );
      });
    };
    animateCounters();

    // Update time
    const updateTime = () => {
      const now = new Date();
      const timeEl = document.getElementById('time');
      if (timeEl) {
        timeEl.textContent = now.toLocaleTimeString('en-US', { hour12: false });
      }
    };
    updateTime();
    const timeInterval = setInterval(updateTime, 1000);

    // DNA helix floating animation
    gsap.to('.dna-helix', {
      y: 20,
      duration: 4,
      ease: "sine.inOut",
      yoyo: true,
      repeat: -1
    });

    // Floating molecules animation
    gsap.utils.toArray('.floating-molecule').forEach((mol, i) => {
      gsap.to(mol, {
        y: gsap.utils.random(-30, 30),
        x: gsap.utils.random(-20, 20),
        rotation: gsap.utils.random(-15, 15),
        duration: gsap.utils.random(3, 5),
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
        delay: i * 0.3
      });
    });

    // Hero text animations
    gsap.fromTo('.hero-line', 
      { y: 100, opacity: 0 },
      { 
        y: 0, 
        opacity: 1, 
        duration: 1.2, 
        stagger: 0.12,
        ease: "power4.out",
        delay: 0.3
      }
    );

    // Section animations
    sectionsRef.current.forEach((section) => {
      if (!section) return;
      
      const heading = section.querySelector('.section-heading');
      const content = section.querySelector('.section-content');
      
      if (heading) {
        gsap.fromTo(heading,
          { y: 80, opacity: 0 },
          {
            scrollTrigger: {
              trigger: section,
              start: "top 80%",
              toggleActions: "play none none reverse"
            },
            y: 0,
            opacity: 1,
            duration: 1,
            ease: "power3.out"
          }
        );
      }
      
      if (content) {
        gsap.fromTo(content,
          { y: 60, opacity: 0 },
          {
            scrollTrigger: {
              trigger: section,
              start: "top 70%",
              toggleActions: "play none none reverse"
            },
            y: 0,
            opacity: 1,
            duration: 1,
            delay: 0.2,
            ease: "power3.out"
          }
        );
      }
    });

    // Parallax cards
    gsap.utils.toArray('.parallax-card').forEach((card, i) => {
      gsap.fromTo(card,
        { y: 100, opacity: 0, rotateY: -10 },
        {
          scrollTrigger: {
            trigger: card,
            start: "top 90%",
            toggleActions: "play none none reverse"
          },
          y: 0,
          opacity: 1,
          rotateY: 0,
          duration: 0.8,
          delay: i * 0.08,
          ease: "power3.out"
        }
      );
    });

    // Horizontal scroll for showcase
    const showcase = document.querySelector('.showcase-track');
    if (showcase) {
      const totalWidth = showcase.scrollWidth - window.innerWidth;
      gsap.to(showcase, {
        scrollTrigger: {
          trigger: '.showcase-section',
          start: "top top",
          end: () => `+=${totalWidth}`,
          scrub: 1,
          pin: true,
          anticipatePin: 1,
        },
        x: -totalWidth,
        ease: "none"
      });
    }

    return () => {
      clearInterval(timeInterval);
      ScrollTrigger.getAll().forEach(t => t.kill());
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const addToRefs = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  return (
    <div className={`app ${isDark ? 'dark-mode' : 'light-mode'}`}>
      {/* Custom Cursor */}
      <div 
        ref={cursorRef} 
        className={`custom-cursor ${isHovering ? 'hovering' : ''}`}
      />
      <div ref={cursorDotRef} className="cursor-dot" />

      {/* Scroll Progress Bar */}
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />

      {/* Floating Particles */}
      <div className="particles">
        {[...Array(20)].map((_, i) => (
          <div 
            key={i} 
            className="particle" 
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      {/* Noise Overlay */}
      <div className="noise-overlay"></div>

      {/* Navigation */}
      <nav className="nav">
        <div className="nav-left">
          <span className="nav-tag">Physician</span>
          <span className="nav-tag">Public Health</span>
          <span className="nav-tag">Advocate</span>
        </div>
        <a href="#home" className="logo">Dr. Inijesu Taiwo</a>
        <div className="nav-right">
          <span className="nav-time" id="time">00:00:00</span>
          <a 
            href="#contact" 
            className="nav-contact magnetic-btn"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >Contact</a>
        </div>
      </nav>

      {/* Floating Theme Toggle */}
      <button 
        className="theme-toggle-floating" 
        onClick={() => setIsDark(!isDark)}
        aria-label="Toggle theme"
      >
        {isDark ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="5"/>
            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        )}
      </button>

      {/* Hero Section */}
      <section id="home" className="hero" ref={heroRef}>
        {/* Large DNA Animation */}
        <div className="hero-dna">
          <div className="dna-helix">
            {[...Array(16)].map((_, i) => (
              <div key={i} className="dna-pair" style={{ animationDelay: `${i * 0.12}s` }}>
                <div className="dna-dot left"></div>
                <div className="dna-connector"></div>
                <div className="dna-dot right"></div>
              </div>
            ))}
          </div>
          <div className="pulse-ring"></div>
          <div className="pulse-ring delay-1"></div>
          <div className="pulse-ring delay-2"></div>
        </div>
        <div className="hero-content">
          <div className="hero-eyebrow hero-line">
            <span className="eyebrow-line"></span>
            <span>MD, MPH • Women's Health • Global Impact</span>
          </div>
          <h1 className="hero-title">
            <span className="hero-line">Transforming</span>
            <span className="hero-line"><em>Healthcare</em></span>
            <span className="hero-line">One Life at</span>
            <span className="hero-line">a Time.</span>
          </h1>
          <p className="hero-subtitle hero-line">
            A physician and public health professional dedicated to advancing 
            women's health, health literacy, and social justice across three continents.
          </p>
        </div>
        <div className="scroll-cta hero-line">
          <span>Scroll to explore</span>
          <div className="scroll-arrow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12l7 7 7-7"/>
            </svg>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section section-dark" ref={addToRefs}>
        {/* Floating Molecules */}
        <div className="section-visuals">
          <div className="floating-molecule mol-1">
            <svg viewBox="0 0 100 100">
              <circle cx="50" cy="30" r="12" />
              <circle cx="30" cy="70" r="10" />
              <circle cx="70" cy="70" r="10" />
              <line x1="50" y1="42" x2="35" y2="60" />
              <line x1="50" y1="42" x2="65" y2="60" />
            </svg>
          </div>
          <div className="floating-molecule mol-2">
            <svg viewBox="0 0 80 80">
              <circle cx="40" cy="20" r="8" />
              <circle cx="20" cy="50" r="8" />
              <circle cx="60" cy="50" r="8" />
              <circle cx="40" cy="70" r="6" />
              <line x1="40" y1="28" x2="25" y2="43" />
              <line x1="40" y1="28" x2="55" y2="43" />
              <line x1="28" y1="55" x2="40" y2="65" />
              <line x1="52" y1="55" x2="40" y2="65" />
            </svg>
          </div>
        </div>
        <div className="section-grid">
          <div className="section-heading">
            <span className="section-number">01</span>
            <h2>We specialize in<br/><em>saving lives.</em></h2>
          </div>
          <div className="section-content">
            <p className="large-text">
              Dr. Inijesu Taiwo's journey in medicine began with a simple yet powerful 
              conviction: healthcare is a fundamental human right.
            </p>
            <p>
              With an MD and MPH, she brings a unique perspective that bridges clinical 
              practice with population health strategy. Her work spans three continents, 
              from community clinics in Nigeria to research institutions in the United States.
            </p>
          </div>
        </div>
      </section>

      {/* Expertise Cards */}
      <section className="section section-cards" ref={addToRefs}>
        <div className="section-heading centered">
          <span className="section-number">02</span>
          <h2>Areas of<br/><em>Expertise</em></h2>
        </div>
        <div className="cards-grid">
          <div className="parallax-card">
            <div className="card-number">01</div>
            <h3>Clinical Medicine</h3>
            <p>Comprehensive patient care with focus on preventive medicine and chronic disease management.</p>
            <div className="card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
              </svg>
            </div>
          </div>
          <div className="parallax-card">
            <div className="card-number">02</div>
            <h3>Public Health Research</h3>
            <p>Evidence-based approaches to health policy, epidemiology, and community interventions.</p>
            <div className="card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
          </div>
          <div className="parallax-card">
            <div className="card-number">03</div>
            <h3>Women's Health</h3>
            <p>Specialized expertise in reproductive health, maternal care, and gender-specific health issues.</p>
            <div className="card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </div>
          </div>
          <div className="parallax-card">
            <div className="card-number">04</div>
            <h3>Global Health</h3>
            <p>International health initiatives spanning Nigeria, Saint Kitts, and the United States.</p>
            <div className="card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 2v20M2 12h20"/>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Horizontal Scroll */}
      <section className="showcase-section">
        <div className="showcase-track">
          <div className="showcase-intro">
            <span className="section-number">03</span>
            <h2>The Journey<br/><em>So Far</em></h2>
            <p>A path defined by passion, purpose, and perseverance.</p>
          </div>
          <div className="showcase-item">
            <div className="showcase-year">Education</div>
            <h3>Medical Training</h3>
            <p>Completed rigorous medical training followed by a Master's in Public Health.</p>
          </div>
          <div className="showcase-item">
            <div className="showcase-year">Nigeria</div>
            <h3>Community Impact</h3>
            <p>Led grassroots health education programs in underserved communities.</p>
          </div>
          <div className="showcase-item">
            <div className="showcase-year">Caribbean</div>
            <h3>Clinical Excellence</h3>
            <p>Expanded clinical experience in Saint Kitts with diverse patient care.</p>
          </div>
          <div className="showcase-item">
            <div className="showcase-year">United States</div>
            <h3>Research & Advocacy</h3>
            <p>Advancing women's health research and health literacy initiatives.</p>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="section section-impact" ref={addToRefs}>
        {/* Heartbeat Animation */}
        <div className="section-visuals">
          <svg className="heartbeat-bg" viewBox="0 0 400 100">
            <path className="heartbeat-path" d="M0,50 L80,50 L100,50 L110,20 L130,80 L150,30 L170,60 L190,50 L280,50 L300,50 L310,20 L330,80 L350,30 L370,60 L390,50 L400,50" />
          </svg>
        </div>
        <div className="section-heading centered">
          <span className="section-number">04</span>
          <h2>Making a<br/><em>Difference</em></h2>
        </div>
        <div className="section-content">
          <div className="impact-stats">
            <div className="stat-item parallax-card">
              <span className="stat-number" data-count="3+">0</span>
              <span className="stat-label">Continents</span>
            </div>
            <div className="stat-item parallax-card">
              <span className="stat-number" data-count="1000+">0</span>
              <span className="stat-label">Lives Touched</span>
            </div>
            <div className="stat-item parallax-card">
              <span className="stat-number">∞</span>
              <span className="stat-label">Dedication</span>
            </div>
          </div>
          <div className="impact-areas">
            <div className="impact-card parallax-card">
              <h3>Women's Health</h3>
              <p>Championing reproductive health education and maternal care access.</p>
            </div>
            <div className="impact-card parallax-card">
              <h3>Health Literacy</h3>
              <p>Empowering communities through accessible health education.</p>
            </div>
            <div className="impact-card parallax-card">
              <h3>Social Justice</h3>
              <p>Advocating for equitable healthcare access for all.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Research Posters Section */}
      <section className="section section-posters" ref={addToRefs}>
        <div className="section-heading centered">
          <span className="section-number">05</span>
          <h2>Research &<br/><em>Publications</em></h2>
          <p className="section-subtitle">Academic posters and presentations advancing public health knowledge</p>
        </div>
        <div className="posters-grid">
          <a 
            href="/assets/ACOG District II.pdf" 
            target="_blank" 
            rel="noopener noreferrer"
            className="poster-card parallax-card"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className="poster-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div className="poster-content">
              <span className="poster-tag">ACOG 2024</span>
              <h3>District II Research Presentation</h3>
              <p>American College of Obstetricians and Gynecologists district conference poster.</p>
            </div>
            <div className="poster-download">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <span>View PDF</span>
            </div>
          </a>

          <a 
            href="/assets/ACOG_2024_District_IVVI_eposter.pptx.pdf" 
            target="_blank" 
            rel="noopener noreferrer"
            className="poster-card parallax-card"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className="poster-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div className="poster-content">
              <span className="poster-tag">ACOG 2024</span>
              <h3>District IV/VI E-Poster</h3>
              <p>Electronic poster presentation for ACOG District IV and VI conference.</p>
            </div>
            <div className="poster-download">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <span>View PDF</span>
            </div>
          </a>

          <a 
            href="/assets/ACOG_2025_District_VIVII_eposter.pdf" 
            target="_blank" 
            rel="noopener noreferrer"
            className="poster-card parallax-card"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className="poster-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div className="poster-content">
              <span className="poster-tag">ACOG 2025</span>
              <h3>District VI/VII E-Poster</h3>
              <p>Latest research presentation for ACOG District VI and VII conference.</p>
            </div>
            <div className="poster-download">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <span>View PDF</span>
            </div>
          </a>

          <a 
            href="/assets/IPHA Poster 2.pdf" 
            target="_blank" 
            rel="noopener noreferrer"
            className="poster-card parallax-card"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className="poster-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div className="poster-content">
              <span className="poster-tag">IPHA</span>
              <h3>Illinois Public Health Association</h3>
              <p>Research presentation at the Illinois Public Health Association conference.</p>
            </div>
            <div className="poster-download">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <span>View PDF</span>
            </div>
          </a>

          <a 
            href="/assets/Maternal and Infant Mortality in Nigeria.pptx.pdf" 
            target="_blank" 
            rel="noopener noreferrer"
            className="poster-card parallax-card featured"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className="poster-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div className="poster-content">
              <span className="poster-tag">Featured Research</span>
              <h3>Maternal & Infant Mortality in Nigeria</h3>
              <p>Comprehensive analysis of maternal and infant mortality challenges and interventions in Nigeria.</p>
            </div>
            <div className="poster-download">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <span>View PDF</span>
            </div>
          </a>

          <a 
            href="/assets/NYSAFP 2025.pdf" 
            target="_blank" 
            rel="noopener noreferrer"
            className="poster-card parallax-card"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className="poster-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div className="poster-content">
              <span className="poster-tag">NYSAFP 2025</span>
              <h3>NY State Academy of Family Physicians</h3>
              <p>Research presentation at the New York State Academy of Family Physicians conference.</p>
            </div>
            <div className="poster-download">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <span>View PDF</span>
            </div>
          </a>

          <a 
            href="/assets/NYSARH Poster.pdf" 
            target="_blank" 
            rel="noopener noreferrer"
            className="poster-card parallax-card"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className="poster-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div className="poster-content">
              <span className="poster-tag">NYSARH</span>
              <h3>NY State Association for Rural Health</h3>
              <p>Research addressing healthcare challenges in rural New York communities.</p>
            </div>
            <div className="poster-download">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <span>View PDF</span>
            </div>
          </a>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section section-contact" ref={addToRefs}>
        <div className="contact-wrapper">
          <div className="section-heading">
            <span className="section-number">06</span>
            <h2>Let's<br/><em>Connect</em></h2>
          </div>
          <div className="section-content">
            <p className="large-text">
              Interested in collaboration, speaking engagements, or just want to say hello?
            </p>
            <a href="mailto:hi@inijesu.com" className="contact-email">
              hi@inijesu.com
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 17L17 7M17 7H7M17 7v10"/>
              </svg>
            </a>
            <div className="social-links">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">LinkedIn</a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">Twitter</a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-left">
            <span>Dr. Inijesu Taiwo</span>
            <span>MD, MPH</span>
          </div>
          <div className="footer-center">
            <span>© 2024 All rights reserved</span>
          </div>
          <div className="footer-right">
            <span>Transforming healthcare,</span>
            <span>one life at a time.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
